// src/components/SocialPickCard.jsx
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthGate";
import { createSocialComment, fetchSocialComments } from "../api";
import { formatMarketName } from "../utils/formatMarketName";

const fmt = (n, d = 2) => (n == null || Number.isNaN(+n) ? "—" : (+n).toFixed(d));

export default function SocialPickCard({
  pick,
  reactionState,
  reactionLoading = false,
  reactionError = "",
  onToggleReaction,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [likedLocal, setLikedLocal] = useState(false);
  const [likesLocal, setLikesLocal] = useState(pick.likes || 0);
  const [showLegs, setShowLegs] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentsError, setCommentsError] = useState("");
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentSaving, setCommentSaving] = useState(false);
  const displayName = pick.tipster_name || pick.tipster_handle || pick.tipster_username || "User";
  const handleText = pick.tipster_handle || (pick.tipster_username ? `@${pick.tipster_username}` : "");
  const profileIdentifier = pick.profile_identifier || pick.tipster_username || null;
  const isAcca =
    (pick.bet_type || "").toLowerCase() === "acca" ||
    pick.is_acca === true ||
    (Array.isArray(pick.acca_legs) && pick.acca_legs.length > 0);
  const accaLegs = Array.isArray(pick.acca_legs) ? pick.acca_legs : [];
  const commentTargetType =
    pick.comment_target_type || (pick._source === "user" ? "user_bet" : "tipster_pick");
  const commentTargetId = pick.comment_target_id || pick.id;
  const liked = reactionState ? !!reactionState.reacted_by_me : likedLocal;
  const likes = reactionState ? Number(reactionState.count || 0) : likesLocal;

  const handleLike = () => {
    if (typeof onToggleReaction === "function") {
      onToggleReaction();
      return;
    }
    setLikedLocal(!likedLocal);
    setLikesLocal(likedLocal ? likesLocal - 1 : likesLocal + 1);
  };

  const handleTrackBet = () => {
    const params = new URLSearchParams({
      fixture_id: pick.fixture_id ? String(pick.fixture_id) : "",
      teams: pick.fixture_label || "",
      market: pick.market || "",
      bookmaker: pick.bookmaker || "",
      price: pick.price != null ? String(pick.price) : "",
      stake: pick.stake != null ? String(pick.stake) : "1",
      notes: `From ${displayName}`,
    });
    if (isAcca) {
      params.set("bet_type", "acca");
      params.set("acca_legs", JSON.stringify(accaLegs));
      if (pick.teams) params.set("teams", pick.teams);
    }
    navigate(`/bets?${params.toString()}`);
  };

  const loadComments = async () => {
    if (!commentTargetId) return;
    setCommentsLoading(true);
    setCommentsError("");
    try {
      const data = await fetchSocialComments({
        target_type: commentTargetType,
        target_id: commentTargetId,
        limit: 100,
      });
      const rows = Array.isArray(data) ? data : data?.comments || [];
      setComments(rows);
      setCommentsLoaded(true);
    } catch (err) {
      console.error("Failed to load comments", err);
      setCommentsError("Comments unavailable.");
    } finally {
      setCommentsLoading(false);
    }
  };

  const toggleComments = () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !commentsLoaded) loadComments();
  };

  const submitComment = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    const body = commentText.trim();
    if (!body || !commentTargetId) return;
    setCommentSaving(true);
    setCommentsError("");
    try {
      const created = await createSocialComment({
        target_type: commentTargetType,
        target_id: commentTargetId,
        body,
      });
      setComments((prev) => [created, ...prev]);
      setCommentText("");
    } catch (err) {
      console.error("Failed to post comment", err);
      setCommentsError("Could not post comment.");
    } finally {
      setCommentSaving(false);
    }
  };

  const getResultColor = (result) => {
    if (!result) return { bg: "rgba(255,255,255,0.05)", color: "#999" };
    const res = result.toUpperCase();
    if (res === "WON" || res === "WIN") return { bg: "rgba(29,185,84,0.15)", color: "#1db954" };
    if (res === "LOST" || res === "LOSE") return { bg: "rgba(210,59,59,0.15)", color: "#d23b3b" };
    if (res === "VOID" || res === "VOIDED") return { bg: "rgba(255,193,7,0.15)", color: "#ffc107" };
    return { bg: "rgba(255,255,255,0.05)", color: "#999" };
  };

  const resultStyle = getResultColor(pick.result);
  const profit = pick.profit || 0;
  const timeAgo = pick.created_at ? getTimeAgo(new Date(pick.created_at)) : "";

  const HeaderIdentity = (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        textDecoration: "none",
        color: "inherit"
      }}
    >
      {pick.tipster_avatar ? (
        <img
          src={pick.tipster_avatar}
          alt={pick.tipster_name}
          style={{
            width: 40,
            height: 40,
            borderRadius: "50%",
            objectFit: "cover",
            border: "2px solid rgba(52,211,153,0.3)"
          }}
        />
      ) : (
        <div style={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          background: "rgba(52,211,153,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 16,
          fontWeight: 700,
          color: "#34d399"
        }}>
          {displayName[0].toUpperCase()}
        </div>
      )}
      <div>
        <div style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>
          {displayName}
        </div>
        <div style={{ fontSize: 13, opacity: 0.6 }}>
          {handleText} {timeAgo && `• ${timeAgo}`}
        </div>
      </div>
    </div>
  );

  return (
    <div style={{
      background: "#0a0f0c",
      borderRadius: 16,
      padding: 20,
      border: "1px solid rgba(255,255,255,0.08)",
      transition: "border-color 0.2s, box-shadow 0.2s",
      cursor: "pointer",
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.borderColor = "rgba(52,211,153,0.3)";
      e.currentTarget.style.boxShadow = "0 4px 20px rgba(52,211,153,0.1)";
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
      e.currentTarget.style.boxShadow = "none";
    }}
    >
      {/* Header: User Info */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        {profileIdentifier ? (
          <Link to={`/users/${profileIdentifier}`} style={{ textDecoration: "none", color: "inherit" }}>
            {HeaderIdentity}
          </Link>
        ) : HeaderIdentity}

        {/* Result Badge */}
        {pick.result && (
          <div style={{
            padding: "6px 12px",
            borderRadius: 20,
            background: resultStyle.bg,
            color: resultStyle.color,
            fontSize: 12,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: 0.5
          }}>
            {pick.result}
          </div>
        )}
      </div>

      {/* Pick Content */}
      <div style={{ marginBottom: 16 }}>
        {/* Fixture */}
        <div style={{
          fontSize: 18,
          fontWeight: 700,
          marginBottom: 12,
          color: "#fff",
          lineHeight: 1.4
        }}>
          {pick.fixture_label || "Match"}
          {isAcca && (
            <span style={{
              marginLeft: 10,
              padding: "3px 10px",
              borderRadius: 999,
              fontSize: 11,
              letterSpacing: 0.5,
              textTransform: "uppercase",
              fontWeight: 700,
              color: "#34d399",
              background: "rgba(52,211,153,0.14)",
              verticalAlign: "middle"
            }}>
              Acca
            </span>
          )}
        </div>

        {/* Market & Details */}
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
          gap: 12,
          background: "rgba(0,0,0,0.4)",
          padding: 16,
          borderRadius: 12,
          border: "1px solid rgba(255,255,255,0.05)"
        }}>
          <div>
            <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Market</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#34d399" }}>
              {formatMarketName(pick.market)}
            </div>
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Odds</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{fmt(pick.price, 2)}</div>
          </div>
          <div>
            <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Stake</div>
            <div style={{ fontSize: 14, fontWeight: 600 }}>{fmt(pick.stake, 1)} units</div>
          </div>
          {pick.bookmaker && (
            <div>
              <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.5 }}>Bookmaker</div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{pick.bookmaker}</div>
            </div>
          )}
        </div>

        {isAcca && accaLegs.length > 0 && (
          <div style={{
            marginTop: 12,
            borderRadius: 12,
            border: "1px solid rgba(255,255,255,0.08)",
            background: "rgba(0,0,0,0.28)",
            padding: 12
          }}>
            <button
              type="button"
              onClick={() => setShowLegs((prev) => !prev)}
              style={{
                border: "1px solid rgba(255,255,255,0.25)",
                background: "rgba(0,0,0,0.5)",
                color: "#fff",
                borderRadius: 999,
                padding: "5px 12px",
                fontSize: 12,
                cursor: "pointer"
              }}
            >
              {showLegs ? "Hide acca legs" : "Show acca legs"}
            </button>
            {showLegs && (
              <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 7 }}>
                {accaLegs.map((leg, idx) => (
                  <div key={`${pick.id}-leg-${idx}`} style={{
                    display: "grid",
                    gridTemplateColumns: "26px 1.5fr 1.1fr 0.9fr 64px",
                    gap: 8,
                    padding: "7px 8px",
                    borderRadius: 8,
                    background: "rgba(0,0,0,0.45)",
                    fontSize: 12
                  }}>
                    <span style={{ opacity: 0.75 }}>{idx + 1}.</span>
                    <span>{leg?.teams || "—"}</span>
                    <span>{formatMarketName(leg?.market || "—")}</span>
                    <span>{leg?.bookmaker || "—"}</span>
                    <span>{fmt(leg?.price, 2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Profit/Loss if settled */}
        {pick.result && (
          <div style={{
            marginTop: 12,
            padding: "12px 16px",
            borderRadius: 12,
            background: profit >= 0 ? "rgba(29,185,84,0.1)" : "rgba(210,59,59,0.1)",
            border: `1px solid ${profit >= 0 ? "rgba(29,185,84,0.2)" : "rgba(210,59,59,0.2)"}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            <span style={{ fontSize: 13, opacity: 0.8 }}>Profit/Loss</span>
            <span style={{
              fontSize: 18,
              fontWeight: 700,
              color: profit >= 0 ? "#1db954" : "#d23b3b"
            }}>
              {profit >= 0 ? "+" : ""}{fmt(profit, 2)} units
            </span>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: 16,
        paddingTop: 16,
        borderTop: "1px solid rgba(255,255,255,0.08)"
      }}>
        {/* Like Button */}
        <button
          onClick={handleLike}
          disabled={reactionLoading}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 999,
            border: "none",
            background: liked ? "rgba(52,211,153,0.15)" : "transparent",
            color: liked ? "#34d399" : "rgba(255,255,255,0.6)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => {
            if (!liked) e.currentTarget.style.background = "rgba(255,255,255,0.05)";
          }}
          onMouseLeave={(e) => {
            if (!liked) e.currentTarget.style.background = "transparent";
          }}
        >
          <span style={{ fontSize: 16 }}>{liked ? "❤️" : "🤍"}</span>
          <span>{reactionLoading ? "…" : likes > 0 ? likes : ""}</span>
        </button>

        {/* Comment Button (future) */}
        <button
          type="button"
          onClick={toggleComments}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "8px 16px",
            borderRadius: 999,
            border: "none",
            background: "transparent",
            color: "rgba(255,255,255,0.6)",
            fontSize: 13,
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.2s"
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(255,255,255,0.05)"}
          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
        >
          <span style={{ fontSize: 16 }}>💬</span>
          <span>Comment {comments.length ? `(${comments.length})` : ""}</span>
        </button>

        {/* Track Bet Button */}
        {user && (
          <button
            onClick={handleTrackBet}
            style={{
              marginLeft: "auto",
              padding: "8px 20px",
              borderRadius: 999,
              border: "1px solid #34d399",
              background: "rgba(52,211,153,0.1)",
              color: "#34d399",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              transition: "all 0.2s"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#34d399";
              e.currentTarget.style.color = "#000";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "rgba(52,211,153,0.1)";
              e.currentTarget.style.color = "#34d399";
            }}
          >
            📊 Track Bet
          </button>
        )}
      </div>

      {showComments && (
        <div style={{
          marginTop: 12,
          borderTop: "1px solid rgba(255,255,255,0.08)",
          paddingTop: 12
        }}>
          {commentsLoading && <div style={{ fontSize: 13, opacity: 0.7 }}>Loading comments…</div>}
          {commentsError && <div style={{ fontSize: 13, color: "#f87171", marginBottom: 8 }}>{commentsError}</div>}

          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={user ? "Write a comment…" : "Log in to comment"}
              disabled={!user}
              style={{
                flex: 1,
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.18)",
                background: "rgba(0,0,0,0.45)",
                color: "#fff",
                fontSize: 13,
                padding: "8px 10px"
              }}
            />
            <button
              type="button"
              onClick={submitComment}
              disabled={!user || commentSaving || !commentText.trim()}
              style={{
                borderRadius: 999,
                border: "1px solid #34d399",
                background: "rgba(52,211,153,0.12)",
                color: "#34d399",
                fontWeight: 700,
                fontSize: 12,
                padding: "7px 12px",
                cursor: "pointer"
              }}
            >
              {commentSaving ? "Posting…" : "Post"}
            </button>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {comments.map((c, idx) => (
              <div key={c.id || `${c.user_id || "u"}-${idx}`} style={{
                borderRadius: 10,
                border: "1px solid rgba(255,255,255,0.08)",
                background: "rgba(0,0,0,0.35)",
                padding: "8px 10px"
              }}>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 4 }}>
                  {c.display_name || c.username || c.user_name || "User"}
                </div>
                <div style={{ fontSize: 13, lineHeight: 1.35 }}>{c.body || c.comment || ""}</div>
              </div>
            ))}
            {!commentsLoading && !comments.length && (
              <div style={{ fontSize: 12, opacity: 0.6 }}>No comments yet.</div>
            )}
          </div>
        </div>
      )}
      {reactionError ? (
        <div style={{ marginTop: 8, fontSize: 12, color: "#f87171" }}>{reactionError}</div>
      ) : null}
    </div>
  );
}

// Helper function to get "time ago" format
function getTimeAgo(date) {
  const seconds = Math.floor((new Date() - date) / 1000);

  const intervals = {
    year: 31536000,
    month: 2592000,
    week: 604800,
    day: 86400,
    hour: 3600,
    minute: 60
  };

  for (const [unit, secondsInUnit] of Object.entries(intervals)) {
    const interval = Math.floor(seconds / secondsInUnit);
    if (interval >= 1) {
      return `${interval}${unit[0]}`;
    }
  }

  return "now";
}
